// src/pages/AdminDashboard/UserManagement.js
import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { MdDelete, MdSearch, MdRefresh, MdSort } from 'react-icons/md';
import { BsTrash } from 'react-icons/bs';
import './UserManagement.css';

export const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'last_active', direction: 'desc' });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [updatingRole, setUpdatingRole] = useState(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await api.get('/admin/users');
            setUsers(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch users');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (userId, newRole) => {
        try {
            await api.put(`/admin/users/${userId}/role`, { role: newRole });
            setUsers(users.map(user =>
                user.id === userId ? { ...user, role: newRole } : user
            ));
        } catch (err) {
            setError('Failed to update user role');
        }
    };

    const handleDelete = (user) => {
        setSelectedUser(user);
        setShowConfirmModal(true);
    };

    const confirmDelete = async () => {
        try {
            await api.delete(`/admin/users/${selectedUser.id}`);
            setUsers(users.filter(u => u.id !== selectedUser.id));
            setShowConfirmModal(false);
            setSelectedUser(null);
        } catch (err) {
            setError('Failed to delete user');
        }
    };

    const handleSort = (key) => {
        setSortConfig({
            key,
            direction: sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc'
        });
    };

    const formatDateTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            timeZone: 'America/Toronto',
            dateStyle: 'medium',
            timeStyle: 'short'
        });
    };

    const sortedUsers = [...users].sort((a, b) => {
        if (sortConfig.key === 'experience') {
            return sortConfig.direction === 'asc'
                ? a.total_experience - b.total_experience
                : b.total_experience - a.total_experience;
        }
        return sortConfig.direction === 'asc'
            ? a[sortConfig.key]?.localeCompare(b[sortConfig.key])
            : b[sortConfig.key]?.localeCompare(a[sortConfig.key]);
    });

    const filteredUsers = sortedUsers.filter(user =>
        user.email.toLowerCase().includes(search.toLowerCase()) ||
        (user.display_name && user.display_name.toLowerCase().includes(search.toLowerCase()))
    );

    return (
        <div className="admin-users">
            <header className="admin-users-header">
                <h1>User Management</h1>
                <div className="admin-users-controls">
                    <div className="search-bar">
                        <MdSearch />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <button onClick={fetchUsers} className="refresh-button">
                        <MdRefresh /> Refresh
                    </button>
                </div>
            </header>

            {error && <div className="error-message">{error}</div>}

            {loading ? (
                <div className="loading">Loading users...</div>
            ) : filteredUsers.length === 0 ? (
                <div className="empty-state">No users found</div>
            ) : (
                <div className="users-table-container">
                    <table className="users-table">
                        <thead>
                            <tr>
                                <th>Profile</th>
                                <th onClick={() => handleSort('email')}>
                                    Email <MdSort />
                                </th>
                                <th onClick={() => handleSort('last_active')}>
                                    Last Active (EST) <MdSort />
                                </th>
                                <th onClick={() => handleSort('experience')}>
                                    Experience <MdSort />
                                </th>
                                <th>Role</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredUsers.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        <img
                                            src={user.profile_picture || "https://via.placeholder.com/40"}
                                            alt="Profile"
                                            className="user-avatar"
                                        />
                                    </td>
                                    <td>{user.email}</td>
                                    <td>{formatDateTime(user.last_active)}</td>
                                    <td>{user.total_experience} XP</td>
                                    <td>
                                        <select
                                            value={user.role}
                                            onChange={(e) => handleRoleChange(user.id, e.target.value)}
                                            className="role-select"
                                        >
                                            <option value="user">User</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </td>
                                    <td>
                                        <button
                                            onClick={() => handleDelete(user)}
                                            className="delete-button"
                                            title="Delete user"
                                        >
                                            <BsTrash />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showConfirmModal && (
                <div className="modal-overlay">
                    <div className="confirm-modal">
                        <h2>Confirm Delete</h2>
                        <p>Are you sure you want to delete user {selectedUser.email}?</p>
                        <p className="warning">This action cannot be undone.</p>
                        <div className="modal-actions">
                            <button onClick={() => setShowConfirmModal(false)}>Cancel</button>
                            <button onClick={confirmDelete} className="delete-button">Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};